import React from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useCart } from "../context/CartContext";
import { useDistributor } from "../context/DistributorContext";
import LanguageSwitcher from "./LanguageSwitcher";
import { ShoppingCart, Mail, Menu, X, Search, ShieldCheck } from "lucide-react";
import useScrollDirection from "../hooks/useScrollDirection";

export default function Header() {
  const { t } = useTranslation();
  const { count } = useCart();
  const { user } = useDistributor();
  const [open, setOpen] = React.useState(false);
  const navigate = useNavigate();
  const visible = useScrollDirection();

  const nav = [
    { to: "/products", label: t("header.clamps") },
    { to: "/products?category=Pipe Shoes", label: t("header.pipe_shoes") },
    { to: "/configurator", label: t("header.custom") },
    { to: "/pipe-scan", label: t("header.scan") },
    { to: "/landing/food-grade-pipe-clamps", label: t("header.industries") },
    { to: "/blog", label: t("header.knowledge_base") },
    { to: "/conversion", label: "DN/Inch" },
    { to: "/contact", label: t("header.contact") },
  ];

  return (
    <>
      {/* Subtle ARAMFIX red top accent stripe */}
      <div
        className="h-[3px] w-full"
        style={{ backgroundColor: "var(--aramfix-red)" }}
        data-testid="header-red-accent"
      />
    <header
      className={`sticky top-0 z-40 border-b-2 border-aramfix-red/30 shadow-sm transition-transform duration-300 ease-out 2xl:translate-y-0 ${
        visible || open ? "translate-y-0" : "-translate-y-full"
      }`}
      style={{ backgroundColor: "var(--nibro-sage)" }}
      data-testid="site-header"
      data-visible={visible ? "true" : "false"}
    >
      <div className="mx-auto flex max-w-[1400px] items-center justify-between gap-3 px-4 py-3 lg:px-8 2xl:max-w-[1720px] 2xl:gap-4 2xl:px-10">
        {/* Logo */}
        <Link to="/" className="flex flex-shrink-0 items-center" data-testid="logo-home-link">
          <img
            src="/aramfix-logo-header.png"
            alt="ARAMFIX — your global fastener supplier · Pipe clamps by NIBRO"
            className="h-8 w-auto max-w-[140px] sm:h-9 sm:max-w-[160px] 2xl:h-10 2xl:max-w-none"
          />
        </Link>

        {/* Desktop Nav — shown from 2xl (≥1536px) only. Below 2xl the
            hamburger menu takes over. The ARAMFIX logo + 8 nav items +
            right-side actions don't fit reliably below this width
            (logo ~280px wide due to embedded tagline), and the previous
            xl breakpoint caused the logo and first nav link to overlap
            on mid-size desktops. */}
        <nav className="hidden min-w-0 flex-1 items-center justify-center gap-0.5 2xl:flex">
          {nav.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.to.replace(/[/?=&]/g, "-")}-link`}
              className={({ isActive }) =>
                `whitespace-nowrap rounded-md px-2.5 py-2 text-[13px] font-semibold uppercase tracking-wide transition-colors ${
                  isActive
                    ? "bg-white/20 text-white"
                    : "text-white/95 hover:bg-white/10"
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        {/* Right side: flags + email + portal + search + cart + hamburger */}
        <div className="flex flex-shrink-0 items-center gap-2">
          {/* Language switcher dropdown — visible from sm. Shows the
              active language (NL) by default; clicking opens the full 25-
              language list with EU flag emojis. Non-active languages are
              marked "Binnenkort" pending translation approval. */}
          <div className="hidden sm:block">
            <LanguageSwitcher />
          </div>
          <a
            href="mailto:info@aramfix.com"
            className="hidden items-center gap-2 rounded-md bg-white/10 px-3 py-2 text-sm font-semibold text-white transition hover:bg-white/20 [@media(min-width:1700px)]:flex"
            data-testid="header-email-link"
          >
            <Mail size={14} />
            <span className="whitespace-nowrap font-mono">info@aramfix.com</span>
          </a>
          {user && user !== false ? (
            <Link
              to="/distributor/dashboard"
              data-testid="header-distributor-dashboard"
              className="hidden items-center gap-1.5 rounded-md bg-emerald-500/20 px-3 py-2 text-xs font-bold uppercase tracking-wide text-white ring-1 ring-emerald-400/50 transition hover:bg-emerald-500/30 lg:flex"
              title={`Distributeur · ${user.markup_pct?.toFixed(1)}% opslag`}
            >
              <ShieldCheck size={14} /> Portal
            </Link>
          ) : (
            <Link
              to="/distributor/login"
              data-testid="header-distributor-login"
              className="hidden items-center gap-1.5 rounded-md bg-white/10 px-3 py-2 text-xs font-bold uppercase tracking-wide text-white transition hover:bg-white/20 lg:flex"
              title="Distributeur login"
            >
              <ShieldCheck size={14} />
              <span className="hidden 2xl:inline">{t("header.distributor_portal")}</span>
            </Link>
          )}
          <button
            type="button"
            onClick={() => navigate("/products")}
            className="hidden h-10 w-10 flex-shrink-0 items-center justify-center rounded-md bg-white/10 text-white transition hover:bg-white/20 sm:flex"
            data-testid="header-search-button"
            aria-label="Zoeken"
          >
            <Search size={16} />
          </button>
          <Link
            to="/cart"
            className="relative flex h-10 flex-shrink-0 items-center gap-2 rounded-md bg-white/10 px-3 text-sm font-semibold text-white transition hover:bg-white/20"
            data-testid="header-cart-link"
          >
            <ShoppingCart size={16} />
            <span className="hidden md:inline">{t("cart.title")}</span>
            {count > 0 && (
              <span
                className="ml-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-aramfix-red px-1 text-[10px] font-bold text-white"
                data-testid="cart-count-badge"
              >
                {count}
              </span>
            )}
          </Link>
          <button
            type="button"
            onClick={() => setOpen(!open)}
            className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-md bg-white/10 text-white 2xl:hidden"
            data-testid="mobile-menu-toggle"
            aria-label="Menu"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      {/* Mobile / tablet Menu (<1536px) */}
      {open && (
        <div className="border-t border-white/20 bg-white 2xl:hidden">
          <div className="mx-auto flex max-w-[1400px] flex-col px-5 py-3">
            {nav.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-${n.to.replace(/[/?=&]/g, "-")}-link`}
                className="border-b border-zinc-200 py-3 text-sm font-semibold uppercase tracking-wide text-zinc-700"
              >
                {n.label}
              </NavLink>
            ))}
            <a
              href="mailto:info@aramfix.com"
              className="border-b border-zinc-200 py-3 text-sm font-semibold uppercase tracking-wide text-aramfix-red"
            >
              info@aramfix.com
            </a>
          </div>
        </div>
      )}
    </header>
    </>
  );
}
